import { Form, Select, Space, Typography } from 'antd';
import { useCallback, useMemo } from 'react';

import type { NDurationSelects } from '@app/components/DurationSelects/DurationSelects.types';
import {
  CALENDAR_PATH,
  EMPLOYEES_PATH,
  RIGHTS_CLIENT_SERVICES,
  RIGHTS_IS_ATTACH,
  RIGHTS_IS_CATEGORY_EDIT,
  RIGHTS_IS_EDIT,
  RIGHTS_RECORDS,
  SERVICES_PATH,
} from '@app/constants';
import { useAccessRights, useWindowSize } from '@app/hooks';
import { hourOptions, minuteOptions } from '@app/store/constants/constants';
import type { IRecordType } from '@app/types';

const { Text } = Typography;

const emptyFn = () => {};

export const DurationSelects: NDurationSelects.Main = ({
  index = 0,
  className = 'duration-selects',
  durationError,
  onMinuteSelect = emptyFn,
  onHourSelect = emptyFn,
  width,
  id,
  defaultHours,
  defaultMinutes,
  setNewData = emptyFn,
}) => {
  const { hasUserRight: rightToEditCategory } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_CATEGORY_EDIT
  );
  const { hasUserRight: rightToAttachEmployees } = useAccessRights(
    RIGHTS_CLIENT_SERVICES,
    RIGHTS_IS_ATTACH
  );
  const { hasUserRight: rightToEditRecord } = useAccessRights(
    RIGHTS_RECORDS,
    RIGHTS_IS_EDIT
  );
  const isEmployeesService = window.location.pathname.includes(EMPLOYEES_PATH);
  const isServiceEdition = window.location.pathname.includes(
    `${SERVICES_PATH}/edit`
  );
  const isRecordsJournalService =
    window.location.pathname.includes(CALENDAR_PATH);
  const { innerWidth } = useWindowSize();
  const pageContentWidth =
    document.querySelector('.ant-tabs')?.getBoundingClientRect().width || null;
  const defaultHoursValue = hourOptions.find(
    ({ label }) => label === `${defaultHours} ч`
  )?.value;
  const defaultMinutesValue = minuteOptions.find(
    ({ label }) => label === `${defaultMinutes} мин`
  )?.value;

  const disabled = useMemo(
    () =>
      (isEmployeesService && !rightToEditCategory) ||
      (isServiceEdition && !rightToAttachEmployees) ||
      (isRecordsJournalService && !rightToEditRecord),
    [
      isEmployeesService,
      rightToEditCategory,
      isServiceEdition,
      rightToAttachEmployees,
      isRecordsJournalService,
      rightToEditRecord,
    ]
  );

  const selectWidth = useMemo(
    () =>
      width
        ? `${width}px`
        : innerWidth < 800
        ? `${Number(pageContentWidth) <= 400 ? pageContentWidth : 400}px`
        : '196px',
    [width, innerWidth, pageContentWidth]
  );

  const onChange = useCallback(
    (field: string) => (value: number) => {
      setNewData((prev: IRecordType) => ({
        ...prev,
        [field]: value,
      }));
    },
    [setNewData]
  );

  return (
    <>
      <Space className="duration-items">
        <Form.Item className="duration-form-item" name={`Hour${index}`}>
          <Select
            data-testid="duration-hours"
            className={className}
            style={{ width: selectWidth }}
            size="large"
            options={hourOptions}
            disabled={disabled}
            defaultValue={defaultHoursValue}
            onSelect={(value) => onHourSelect(value, id)}
            onChange={onChange('Hour0')}
            popupRender={(menu) => (
              <div data-testid="duration-hours-dropdown">{menu}</div>
            )}
          />
        </Form.Item>
        <Form.Item className="duration-form-item" name={`Minute${index}`}>
          <Select
            data-testid="duration-minutes"
            className={className}
            style={{ width: selectWidth }}
            size="large"
            options={minuteOptions}
            disabled={disabled}
            defaultValue={defaultMinutesValue}
            onSelect={(value) => onMinuteSelect(value, id)}
            onChange={onChange('Minute0')}
            popupRender={(menu) => (
              <div data-testid="duration-minutes-dropdown">{menu}</div>
            )}
          />
        </Form.Item>
      </Space>
      {!!durationError && <Text type="danger">{durationError}</Text>}
    </>
  );
};
